import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { Check, Rocket } from 'lucide-react'
import { useState } from 'react'
import { trackEvent } from '../../../lib/gtag'
import type { LaunchStepId } from '../config/launch-checklist'
import { useLaunchFlow } from '../context/launch-flow-context'

// One line per incomplete step, telling the founder what finishing it gets them.
const STEP_HINTS: Record<LaunchStepId, (contactsLabel: string) => string> = {
  profile: (contacts) => `Complete your profile to unlock all ${contacts} contacts.`,
  review: (contacts) => `Review and approve your ${contacts} personalized intros.`,
  linkedin: () => 'Connect your LinkedIn so intros send from your account.',
}

const CHECKLIST_ROWS: Array<{
  id: LaunchStepId
  title: string
  description: string
  cta: string
}> = [
  {
    id: 'profile',
    title: 'Complete your profile',
    description:
      'Fundraising stage, team bios, traction metrics. Feeds your message personalization.',
    cta: 'Open profile',
  },
  {
    id: 'review',
    title: 'Review your messages',
    description: 'Personalized intros. Edit anything. Nothing sends without your approval.',
    cta: 'Review now',
  },
  {
    id: 'linkedin',
    title: 'Connect LinkedIn',
    description:
      'One-time secure connection. About 5 minutes, ends with a LinkedIn two-factor sign-in.',
    cta: 'Connect',
  },
]

// Pre-launch closer at the bottom of the dashboard: after scrolling the value
// (matches, contacts, board), the founder lands on one clear "finish setup"
// hand-rail. Opens the full checklist dialog; launches from there when ready.
export function OutreachLaunchBar() {
  const { startupId, checklist, contactsLabel, openStep, launch, launchPending } = useLaunchFlow()
  const [open, setOpen] = useState(false)
  const ready = checklist.firstIncomplete === null

  const openChecklist = () => {
    trackEvent('outreach_launch_checklist_opened', { startup_id: startupId })
    setOpen(true)
  }

  return (
    <>
      <section className="flex flex-wrap items-center justify-between gap-5 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-card/20 px-6 py-5">
        <div>
          <div className="text-[17px] font-bold tracking-[-0.01em] text-foreground">
            {ready ? 'Ready to launch.' : 'Almost there.'}
          </div>
          <div className="mt-0.5 text-[13px] text-foreground/80">
            {checklist.firstIncomplete
              ? STEP_HINTS[checklist.firstIncomplete.id](contactsLabel)
              : 'All steps done. First messages go out within 24 hours.'}
          </div>
        </div>
        {ready ? (
          <CtaButton onClick={openChecklist} size="lg" arrow>
            Launch campaign
          </CtaButton>
        ) : (
          <SecondaryButton onClick={openChecklist} size="lg" arrow>
            Finish setup to launch
          </SecondaryButton>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{ready ? 'Ready to launch' : 'Finish setup to launch'}</DialogTitle>
            <DialogDescription>
              {ready
                ? `All steps done. Your ${contactsLabel} intros go out over about two weeks, 6 to 8 a day, each one approved by you. Replies land in your LinkedIn inbox. When an investor responds, you take it from there. Pause anytime. Refund anytime.`
                : 'A few short steps before your first intro goes out. Each takes a minute or two.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2.5">
            {CHECKLIST_ROWS.filter((row) => checklist.steps.some((step) => step.id === row.id)).map(
              (row, i) => {
                const done = checklist.steps.find((step) => step.id === row.id)?.done ?? false
                return (
                  <div
                    key={row.id}
                    className="flex items-center gap-3.5 rounded-xl border border-border/60 bg-card/40 px-4 py-3.5"
                  >
                    <span
                      className={cn(
                        'flex size-6.5 shrink-0 items-center justify-center rounded-full text-[11.5px] font-bold',
                        done
                          ? 'bg-emerald-500/15 text-emerald-400'
                          : 'border border-dashed border-border bg-card text-muted-foreground'
                      )}
                    >
                      {done ? <Check className="size-3.5" strokeWidth={3} /> : i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-semibold text-foreground">{row.title}</div>
                      <p className="mt-0.5 text-[11.5px] leading-relaxed text-muted-foreground">
                        {row.description}
                      </p>
                    </div>
                    {done ? (
                      <span className="shrink-0 text-[11.5px] font-semibold text-emerald-400">
                        Done
                      </span>
                    ) : (
                      <SecondaryButton
                        size="sm"
                        arrow
                        onClick={() => {
                          setOpen(false)
                          openStep(row.id)
                        }}
                      >
                        {row.cta}
                      </SecondaryButton>
                    )}
                  </div>
                )
              }
            )}
          </div>

          {ready && (
            <CtaButton
              onClick={launch}
              loading={launchPending}
              size="lg"
              fullWidth
              icon={<Rocket />}
            >
              {launchPending ? 'Launching…' : 'Launch campaign'}
            </CtaButton>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
